import { app } from "js/namespaces";
import React from "reactn";
import * as geom from "js/core/utilities/geom";
import { _ } from "js/vendor";
import { BaseElement } from "js/elements/base/BaseElement";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import {
    SVGPathElement,
    SVGCircleElement
} from "js/elements/base/SVGElement";
import { Shape } from "js/elements/base/Shapes";
import { TextGroup } from "js/elements/base/TextGroup";
import { FormatType, HorizontalAlignType } from "common/constants";
import { formatter } from "js/core/utilities/formatter";
import { TextElement } from "js/elements/base/TextElement";
import { getSVGStyleProps, SVGGroup } from "../../core/utilities/svgHelpers";

class PieChart extends CollectionElement {
    get canSelect() {
        return false;
    }

    get canRollover() {
        return false;
    }

    getChildItemType() {
        return PieChartWedge;
    }

    get collectionPropertyName() {
        return "data";
    }

    get showLegend() {
        return this.model.legendPosition == "left" || this.model.legendPosition == "right";
    }

    getCanvasMargins() {
        return {
            top: app.currentTheme.styleSheet.Canvas.marginTop,
            left: app.currentTheme.styleSheet.Canvas.marginLeft,
            right: app.currentTheme.styleSheet.Canvas.marginRight,
            bottom: 0
        };
    }

    get format() {
        // migration from old formatting
        if (!this.model.format) {
            this.migrateFormat();
        }
        return this.model.format || FormatType.PERCENT;
    }

    // migrate from pre-April 2019 labelFormat model to support FormatOptionsMenu
    migrateFormat() {
        switch (this.model.labelFormat) {
            case "numeric":
                this.model.format = FormatType.NUMBER;
                break;
            case "none":
                this.model.format = FormatType.NONE;
                break;
            case "percentage":
            default:
                this.model.format = FormatType.PERCENT;
                break;
        }
    }

    get formatOptions() {
        return this.model.formatOptions || formatter.getDefaultFormatOptions();
    }

    formatValue(value) {
        let displayValue = value;
        if (this.format == FormatType.PERCENT) {
            let totalValue = _.sumBy(this.model.data, m => m.value);
            displayValue = value / totalValue;
        } else {
            displayValue = value;
        }

        return formatter.formatValue(displayValue, this.format, this.formatOptions);
    }

    _build() {
        let data = this.model.data;
        if (this.model.sortValues) {
            data = _.reverse(_.sortBy(data, o => o.value));
        }

        this.wedges = [];
        for (let i = 0; i < data.length; i++) {
            let id = "wedge" + i;
            let model = data[i];
            model.id = id;
            this.wedges.push(this.addElement(id, () => PieChartWedge, { model }));
        }

        if (this.showLegend) {
            this.legend = this.addElement("legend", () => PieChartLegend);
        }
    }

    _calcProps(props, options) {
        let { size } = props;

        let legendProps;
        let legendSize = new geom.Size(0, 0);
        if (this.showLegend) {
            legendProps = this.legend.calcProps(size);
            legendSize = legendProps.size;
        }

        let pieChartBounds;
        if (this.model.chartScale) {
            // set radius based on size * chartScale
            let r = Math.round(Math.min(size.width - legendSize.width, size.height) / 2) * (this.model.chartScale || 1);
            pieChartBounds = this.calcPieLayout(props, new geom.Size(size.width - legendSize.width, size.height), r);
        } else {
            // legacy pie chart with no chartScale property uses autoFit
            pieChartBounds = this.autoFit(props);
        }

        this.pieChartOffsetX = 0;

        if (this.showLegend) {
            let totalWidth = legendSize.width + this.styles.legendGap + pieChartBounds.width;
            let offsetX = size.width / 2 - totalWidth / 2;

            if (this.model.legendPosition == "left") {
                this.pieChartOffsetX = offsetX + this.styles.legendGap + legendSize.width - pieChartBounds.left;
                legendProps.bounds = new geom.Rect(offsetX, size.height / 2 - legendSize.height / 2, legendSize);
            } else {
                this.pieChartOffsetX = offsetX - pieChartBounds.left - this.styles.legendGap;
                legendProps.bounds = new geom.Rect(offsetX + pieChartBounds.width, size.height / 2 - legendSize.height / 2, legendSize);
            }

            for (let wedge of this.wedges) {
                wedge.centerX += this.pieChartOffsetX;
                wedge.bounds.left += this.pieChartOffsetX;
            }
        }

        return { size: pieChartBounds.size };
    }

    autoFit(props) {
        let { size } = props;

        let r = Math.round(Math.min(props.allowedSize.width, props.allowedSize.height) / 2);

        let totalLabelBounds;
        let attempts = 0;
        while (attempts < 25) {
            totalLabelBounds = this.calcPieLayout(props, props.allowedSize, r);
            if (totalLabelBounds.top > 0 && totalLabelBounds.bottom < props.allowedSize.height && totalLabelBounds.left > 0 && totalLabelBounds.right < props.allowedSize.width) {
                break;
            }
            r -= 5;
            attempts++;
        }

        this.model.chartScale = r / Math.round(Math.min(props.allowedSize.width, props.allowedSize.height) / 2);
        // this.canvas.updateCanvasModel(false);
        return totalLabelBounds;
    }

    calcPieLayout(props, size, radius) {
        let total = _.sumBy(this.model.data, m => m.value);
        let innerRadius = this.model.isDonut ? this.model.innerRadius || this.styles.innerRadius : 0;
        let maxLabelScale = this.model.labelScale || 1;

        let doesIntersect = (source, list) => {
            let intersects = false;
            for (let target of list) {
                if (!target.equals(source) && source.inflate(5).intersects(target.inflate(5))) {
                    intersects = true;
                    break;
                }
            }
            return intersects;
        };

        let doesWedgeLabelIntersect = (wedge, bounds) => {
            let intersects = false;

            let sourceTitle = wedge.label.text.title.bounds.offset(bounds.left, bounds.top).inflate(5);

            let sourceBody;
            if (wedge.label.text.body) {
                sourceBody = wedge.label.text.body.bounds.offset(bounds.left, bounds.top).inflate(5);
            }

            for (let otherWedge of this.wedges) {
                if (wedge.id !== otherWedge.id) {
                    let targetTitle = otherWedge.label.text.title.bounds.offset(otherWedge.label.bounds.left, otherWedge.label.bounds.top).inflate(5);

                    if (sourceTitle.intersects(targetTitle)) {
                        intersects = true;
                        break;
                    }

                    if (sourceBody && otherWedge.label.text.body) {
                        let targetBody = otherWedge.label.text.body.bounds.offset(otherWedge.label.bounds.left, otherWedge.label.bounds.top).inflate(5);
                        if (sourceBody.intersects(targetTitle) || sourceTitle.intersects(targetBody) || sourceBody.intersects(targetBody)) {
                            intersects = true;
                            break;
                        }
                    }
                }
            }
            return intersects;
        };

        let outsideLabelOffset = 10;

        let angle = 0;
        // set wedge options so they can lay themselves out
        let allLabelBounds = this.wedges.map(wedge => {
            wedge.radius = radius;
            wedge.angle = angle;
            wedge.percentage = wedge.model.value / total;
            wedge.startAngle = this.model.startAngle || 0;
            wedge.innerRadius = innerRadius;
            wedge.label.showConnector = false;
            let wedgeProps = wedge.calcProps(size);
            wedgeProps.bounds = new geom.Rect(0, 0, size);

            angle += 360 * wedge.percentage;

            let labelProps;

            if (this.model.labelPosition == "outside") {
                labelProps = wedge.label.calcLabelProps(props, false, Math.min(0.6, maxLabelScale), outsideLabelOffset, 0);
            } else {
                labelProps = wedge.label.calcLabelProps(props, true, maxLabelScale, 0, 0);

                let paddedLabelBounds = labelProps.bounds.inflate(5);
                // check if label intersects with wedge and recalculate label bounds outside radius with smaller scale
                if (paddedLabelBounds.intersectsLine(wedge.edgeLines[0][0], wedge.edgeLines[0][1], wedge.edgeLines[0][2], wedge.edgeLines[0][3]).any || paddedLabelBounds.intersectsLine(wedge.edgeLines[1][0], wedge.edgeLines[1][1], wedge.edgeLines[1][2], wedge.edgeLines[1][3]).any) {
                    labelProps = wedge.label.calcLabelProps(props, false, Math.min(0.6, maxLabelScale), outsideLabelOffset, 0);
                } else if (innerRadius > 0) {
                    labelProps = wedge.label.calcLabelProps(props, false, maxLabelScale, outsideLabelOffset, 0);
                }
            }

            wedge.label.createProps(labelProps);
            return labelProps.bounds;
        });

        //try rotating labels to fit
        rotateLoop:
        for (let i = 0; i < this.wedges.length; i++) {
            let wedge = this.wedges[i];
            if (doesWedgeLabelIntersect(wedge, wedge.label.calculatedProps.bounds)) {
                for (let angleOffset = 0.01; angleOffset < wedge.percentage * Math.PI + Math.PI * 0.01; angleOffset = angleOffset + 0.01) {
                    //  console.log("try", angleOffset);
                    let labelProps = wedge.label.calcLabelProps(props, false, Math.min(0.6, maxLabelScale), outsideLabelOffset, -angleOffset);
                    if (!doesWedgeLabelIntersect(wedge, labelProps.bounds)) {
                        allLabelBounds[i] = labelProps.bounds;
                        // wedge.label.calculatedProps.bounds = labelProps.bounds;
                        wedge.label.showConnector = true;
                        wedge.label.isRotated = true;
                        console.log("fit");
                        break rotateLoop;
                    }
                }
                for (let angleOffset = 0.01; angleOffset < wedge.percentage * Math.PI + Math.PI * 0.01; angleOffset = angleOffset + 0.01) {
                    let labelProps = wedge.label.calcLabelProps(props, false, Math.min(0.6, maxLabelScale), outsideLabelOffset, angleOffset);
                    if (!doesWedgeLabelIntersect(wedge, labelProps.bounds)) {
                        allLabelBounds[i] = labelProps.bounds;
                        // wedge.label.calculatedProps.bounds = labelProps.bounds;
                        wedge.label.showConnector = true;
                        wedge.label.isRotated = true;
                        break rotateLoop;
                    }
                }

                wedge.label.showConnector = false;
                wedge.label.isRotated = false;
                // wedge.label.calculatedProps.bounds = wedge.label.calcLabelProps(props, false, Math.min(0.6, maxLabelScale), outsideLabelOffset, 0);  //reset bounds (required for textAlign reset)
            }
        }

        // if the label is still overlapping other labels, move it outwards until it no longer overlaps
        let offsetLabelBounds = [];
        _.sortBy(this.wedges, w => w.model.value).forEach((wedge, i) => {
            // check for intersection while increasing distance
            let offset = 1;
            offsetLabelBounds.push(wedge.label.calculatedProps.bounds);

            while (doesIntersect(wedge.label.calculatedProps.bounds, offsetLabelBounds)) {
                let labelProps = wedge.label.calcLabelProps(props, false, Math.min(0.6, maxLabelScale), offset, 0);
                offsetLabelBounds[i] = labelProps.bounds;
                wedge.label.showConnector = true;

                if (offset++ >= 300) break;
            }
        });

        let totalLabelBounds = null;
        this.wedges.forEach(wedge => {
            if (totalLabelBounds) {
                totalLabelBounds = totalLabelBounds.union(wedge.label.bounds);
            } else {
                totalLabelBounds = wedge.label.calculatedProps.bounds;
            }
        });

        let pieBounds = new geom.Rect(size.width / 2 - radius, size.height / 2 - radius, radius * 2, radius * 2);
        return pieBounds.union(totalLabelBounds);
    }

    renderChildren(transition) {
        let props = this.calculatedProps;
        let renderChildren = super.renderChildren(transition);

        let lines = [];

        this.wedges.forEach((wedge, idx) => {
            if (wedge.label.showConnector) {
                let connectionPoint = wedge.label.getConnectionPoint();
                lines.push(
                    <line
                        key={idx}
                        {...getSVGStyleProps(this.styles.connectors)}
                        x1={connectionPoint.x + this.pieChartOffsetX}
                        y1={connectionPoint.y}
                        x2={wedge.connectorPoint.x}
                        y2={wedge.connectorPoint.y}
                    />
                );
            }
        });

        if (lines.length) {
            renderChildren.push(<SVGGroup key={this.id}>{lines}</SVGGroup>);
        }

        return renderChildren;
    }

    getAnimations() {
        const animations = this._getAnimations();
        animations.forEach(animation => animation.element = this);

        if (this.animateChildren) {
            Object.values(this.elements)
                // Sorting wedges by their value
                .sort((elementA, elementB) => elementA.model.value - elementB.model.value)
                .forEach(element => {
                    animations.push(...element.getAnimations());
                });
        }

        return animations;
    }
}

class PieChartWedge extends CollectionItemElement {
    get canSelect() {
        return true;
    }

    get canRollover() {
        return false;
    }

    get pieChart() {
        return this.parentElement;
    }

    get wedgeColor() {
        return this.getSlideColor({
            shading: "dark",
            index: this.itemIndex,
            itemCount: this.itemCount
        });
    }

    containsPoint(pt) {
        let selectionBounds = this.selectionBounds;
        let radius = Math.sqrt(Math.pow(selectionBounds.centerH - pt.x, 2) + Math.pow(selectionBounds.centerV - pt.y, 2));
        let angle = Math.radiansToDegrees(Math.atan2(pt.y - selectionBounds.centerV, pt.x - selectionBounds.centerH));

        if (angle < 0) {
            angle = 360 + angle;
        }
        // console.log("start", (this.startAngle + this.angle) % 360, "end", (this.startAngle + this.angle + 360 * this.percentage) % 360, "angle", angle, "radius", radius);

        let startAngle = (this.startAngle + this.angle) % 360;
        let endAngle = (this.startAngle + this.angle + 360 * this.percentage) % 360;
        if (endAngle == 0) endAngle = 360;

        return (
            radius < this.outerRadius &&
            ((angle >= startAngle && angle <= endAngle) ||
                (endAngle < startAngle && angle <= endAngle))
        );
    }

    get connectorPoint() {
        return new geom.Point(this.centerX + this.outerRadius * Math.cos(this.centerAngle), this.centerY + this.outerRadius * Math.sin(this.centerAngle));
    }

    _build() {
        this.shape = this.addElement("shape", () => SVGPathElement);
        this.label = this.addElement("label", () => PieChartLabel, {
            canSelect: true,
            canRollover: true
        });
    }

    _calcProps(props, options) {
        let { size } = props;
        this.centerX = size.width / 2;
        this.centerY = size.height / 2;

        let startAngle = Math.degreesToRadians(this.startAngle + this.angle);
        this.centerAngle = Math.degreesToRadians(this.startAngle + this.angle + 180 * this.percentage);
        let endAngle = Math.degreesToRadians(this.startAngle + this.angle + 360 * this.percentage);
        let offset = this.model.emphasized ? (this.styles.offset || 10) : 0;

        this.outerRadius = this.radius + offset;
        this.innerRadius = this.radius * this.innerRadius / 100;

        let largeArc = Math.abs(startAngle - endAngle) > Math.PI ? 1 : 0;

        this.edgeLines = [];

        if (this.innerRadius > 0) {
            this.edgeLines.push([this.centerX + this.innerRadius * Math.cos(startAngle), this.centerY + this.innerRadius * Math.sin(startAngle), this.centerX + this.outerRadius * Math.cos(startAngle), this.centerY + this.outerRadius * Math.sin(startAngle)]);
            this.edgeLines.push([this.centerX + this.outerRadius * Math.cos(endAngle), this.centerY + this.outerRadius * Math.sin(endAngle), this.centerX + this.innerRadius * Math.cos(endAngle), this.centerY + this.innerRadius * Math.sin(endAngle)]);
        } else {
            this.edgeLines.push([this.centerX, this.centerY, this.centerX + this.outerRadius * Math.cos(startAngle), this.centerY + this.outerRadius * Math.sin(startAngle)]);
            this.edgeLines.push([this.centerX + this.outerRadius * Math.cos(endAngle), this.centerY + this.outerRadius * Math.sin(endAngle), this.centerX, this.centerY]);
        }

        let shapeProps = this.shape.createProps({
            path: Shape.drawWedge(this.centerX, this.centerY, this.innerRadius, this.outerRadius, startAngle, endAngle).toPathData(),
            forceTransition: false,
            layer: -1
        });
        this.shape.styles.fillColor = this.wedgeColor.toRgbString();

        // // set up the label props but they will be calc'd in piechart element
        // children.createProps(this.label);

        return { size };
    }

    get animationElementName() {
        return `Wedge ${this.model.label?.text || this.pieChart.formatValue(this.model.value) || `#${this.itemIndex}`}`;
    }
}

class PieChartLabel extends BaseElement {
    get canSelect() {
        return true;
    }

    get canRollover() {
        return false;
    }

    get wedge() {
        return this.parentElement;
    }

    // get layer() {
    //     if (this.calculatedProps.isOutside) {
    //         return -1;
    //     } else {
    //         return 1;
    //     }
    // }

    _build() {
        let textOptions = {
            canSelect: true,
            canRollover: true,
            title: {
                bindTo: "label",
                allowStyling: false,
                placeholder: "",
                spellcheck: false,
                canSelect: true,
            },
            autoHeight: true,
            autoWidth: true,
            showBody: true,
            body: {
                model: {
                    body: this.wedge.pieChart.formatValue(this.model.value),
                    placeholder: ""
                },
                canEdit: false,
                canSelect: false,
                canRollover: false,
                isTabbable: false,
                spellcheck: false
            }
        };

        if (this.wedge.pieChart.format == FormatType.NONE) {
            textOptions.showBody = false;
            textOptions.showAddButton = false;
        }

        if (this.wedge.pieChart.showLegend) {
            textOptions.showBody = false;
            textOptions.title = {
                bindTo: "title",
                model: {
                    title: this.wedge.pieChart.formatValue(this.model.value)
                },
                canSelect: false
            };
        }

        this.text = this.addElement("text", () => TextGroup, textOptions);
    }

    _calcProps(props, options) {
        let { size } = props;
        let textProps = this.text.calcProps(size);
        return { size: textProps.size };
    }

    getConnectionPoint() {
        if (this.bounds.x < this.wedge.centerX && this.bounds.y < this.wedge.centerY) {
            return this.bounds.getPoint("bottom-right");
        } else if (this.bounds.x >= this.wedge.centerX && this.bounds.y < this.wedge.centerY) {
            return this.bounds.getPoint("bottom-left");
        } else if (this.bounds.x < this.wedge.centerX && this.bounds.y >= this.wedge.centerY) {
            return this.bounds.getPoint("top-right");
        } else {
            return this.bounds.getPoint("top-left");
        }
    }

    calcLabelProps(props, isInside, scale, offset, rotation) {
        let { styles } = props;

        let wedgeStyles = this.wedge.styles;

        let labelDistance;
        if (isInside) {
            labelDistance = this.wedge.outerRadius * .58;
            props.isOutside = false;
        } else {
            labelDistance = this.wedge.outerRadius + offset;
            props.isOutside = true;
            this.text.title && (this.text.styles.title.filter = "textStroke");
            this.text.body && (this.text.styles.body.filter = "textStroke");
        }
        let labelPoint = new geom.Point(this.wedge.centerX + labelDistance * Math.cos(this.wedge.centerAngle + rotation), this.wedge.centerY + labelDistance * Math.sin(this.wedge.centerAngle + rotation));

        if (this.wedge.pieChart.showLegend) {
            this.updateStyles(this.styles.valueOnly, true);
        }

        this.text.scaleStyleValues(scale);

        if (labelPoint.x < this.wedge.centerX) {
            this.text.styles.textAlign = "right";
        } else {
            this.text.styles.textAlign = "left";
        }

        let labelWidth = 170;
        let labelHeight = 300;

        let labelProps = this.calcProps(new geom.Size(labelWidth, labelHeight));
        labelProps.isOutside = !isInside;
        labelWidth = labelProps.size.width;
        labelHeight = labelProps.size.height;

        this.text.calculatedProps.bounds = new geom.Rect(0, 0, labelWidth, labelHeight);

        let labelLoc;
        if (isInside) {
            labelLoc = new geom.Point(labelPoint.x - labelProps.size.width / 2, labelPoint.y - labelProps.size.height / 2);
        } else {
            if (labelPoint.x < this.wedge.centerX && labelPoint.y < this.wedge.centerY) {
                labelLoc = new geom.Point(labelPoint.x - labelWidth, labelPoint.y - labelHeight);
            } else if (labelPoint.x >= this.wedge.centerX && labelPoint.y < this.wedge.centerY) {
                labelLoc = new geom.Point(labelPoint.x, labelPoint.y - labelHeight);
            } else if (labelPoint.x < this.wedge.centerX && labelPoint.y >= this.wedge.centerY) {
                labelLoc = new geom.Point(labelPoint.x - labelWidth, labelPoint.y);
            } else {
                labelLoc = new geom.Point(labelPoint.x, labelPoint.y);
            }
        }

        if (this.hasStoredPropChanged("isOutside", props.isOutside)) {
            this.markStylesAsDirty();
        }

        labelProps.bounds = new geom.Rect(labelLoc, labelProps.size);
        return labelProps;
    }

    getBackgroundColor(forElement) {
        if (forElement && this.wedge && !this.calculatedProps.isOutside && (forElement.id === "title" || forElement.id === "body")) {
            return this.wedge.getSlideColor({
                shading: "dark",
                index: this.wedge.itemIndex,
                itemCount: this.wedge.itemCount
            });
        } else {
            return this.getParentBackgroundColor(forElement);
        }
    }
}

class PieChartLegend extends CollectionElement {
    get canSelect() {
        return false;
    }

    getChildItemType(itemModel) {
        return PieChartLegendItem;
    }

    get collectionPropertyName() {
        return "data";
    }

    _calcProps(props, options) {
        let { size } = props;
        let layouter = this.getLayouter(props, this.itemElements, size);
        layouter.distributeVertically({
            gap: this.styles.vGap,
            horizontalAlign: HorizontalAlignType.LEFT
        });

        props.isFit = true;

        return { size: layouter.size };
    }
}

class PieChartLegendItem extends CollectionItemElement {
    get canSelect() {
        return false;
    }

    _build() {
        this.circle = this.addElement("circle", () => SVGCircleElement);
        this.label = this.addElement("label", () => TextElement, {
            canEdit: true,
            canStyle: false,
            autoWidth: true,
            autoHeight: true
        });
    }

    _calcProps(props, options) {
        let { size } = props;
        let hGap = this.styles.hGap;
        let circleSize = this.styles.circleSize;

        let labelProps = this.label.calcProps(new geom.Size(size.width - hGap - circleSize, size.height));

        let circleProps = this.circle.createProps();
        this.circle.updateStyles({
            fillColor: this.getSlideColor({
                shading: "dark",
                index: this.itemIndex,
                itemCount: this.itemCount
            }).toHexString()
        });
        circleProps.bounds = new geom.Rect(0, labelProps.size.height / 2 - circleSize / 2, circleSize, circleSize);

        labelProps.bounds = new geom.Rect(circleSize + hGap, 0, labelProps.size);
        return { size: new geom.Size(labelProps.size.width + circleSize + hGap, 30) };
    }
}

export { PieChart, PieChartLabel };
